/**
 * Server state for the customer screens. There is no guest event stream, so freshness comes from:
 * - a refetch after every change the customer makes,
 * - polling only on the screen that needs it, only while the page is visible,
 * - TanStack Query's refetch when the tab regains focus.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { isApiError } from '@/api/errors';
import { customerApi } from './api';
import type { AddCartItemInput, AssistanceOutcome, AssistanceType, Cart } from './api';
import { useCustomer } from './context';

/** How often a visible screen re-reads the server. The cart is fastest: a waiter may be adding to it. */
export const POLL_MS = { cart: 5_000, orders: 10_000, bill: 15_000, menu: 30_000 } as const;

export const customerKeys = {
  all: (sessionId: string) => ['guest', sessionId] as const,
  menu: (sessionId: string) => ['guest', sessionId, 'menu'] as const,
  cart: (sessionId: string) => ['guest', sessionId, 'cart'] as const,
  orders: (sessionId: string) => ['guest', sessionId, 'orders'] as const,
  bill: (sessionId: string) => ['guest', sessionId, 'bill'] as const,
  assistance: (sessionId: string) => ['guest', sessionId, 'assistance'] as const,
};

/** With `poll`, the menu screen follows availability set by the kitchen while it stays open. */
export function useMenu(options: { poll?: boolean } = {}) {
  const { session } = useCustomer();
  return useQuery({
    queryKey: customerKeys.menu(session.sessionId),
    queryFn: ({ signal }) => customerApi.getMenu(signal),
    staleTime: 30_000,
    refetchInterval: options.poll ? POLL_MS.menu : false,
  });
}

export function useCart(options: { poll?: boolean; refetchOnMount?: boolean | 'always' } = {}) {
  const { session } = useCustomer();
  return useQuery({
    queryKey: customerKeys.cart(session.sessionId),
    queryFn: ({ signal }) => customerApi.getCart(signal),
    refetchInterval: options.poll ? POLL_MS.cart : false,
    // A screen that shows the cart always starts from the server's current cart, never a cached one.
    refetchOnMount: options.refetchOnMount ?? (options.poll ? 'always' : true),
  });
}

export function useOrders(options: { poll?: boolean } = {}) {
  const { session } = useCustomer();
  return useQuery({
    queryKey: customerKeys.orders(session.sessionId),
    queryFn: ({ signal }) => customerApi.getOrders(signal),
    refetchInterval: options.poll ? POLL_MS.orders : false,
    refetchOnMount: options.poll ? 'always' : true,
  });
}

export function useBill(options: { poll?: boolean } = {}) {
  const { session } = useCustomer();
  return useQuery({
    queryKey: customerKeys.bill(session.sessionId),
    queryFn: ({ signal }) => customerApi.getBill(signal),
    refetchInterval: options.poll ? POLL_MS.bill : false,
    refetchOnMount: options.poll ? 'always' : true,
  });
}

/** Stores a cart the server returned for a change the customer made, and marks it as seen. */
function useAcceptOwnCart() {
  const { session, lastSeenCart } = useCustomer();
  const queryClient = useQueryClient();
  return (cart: Cart) => {
    lastSeenCart.current = cart;
    queryClient.setQueryData(customerKeys.cart(session.sessionId), cart);
  };
}

export function useAddCartItem() {
  const { session } = useCustomer();
  const queryClient = useQueryClient();
  const accept = useAcceptOwnCart();
  return useMutation({
    mutationFn: (vars: { input: AddCartItemInput; idempotencyKey: string }) =>
      customerApi.addCartItem(vars.input, vars.idempotencyKey),
    onSuccess: accept,
    // The server refused an item that sold out: read the menu again so the screen shows it.
    onError: (error) => {
      if (isApiError(error) && error.hasCode('ITEM_OUT_OF_STOCK')) {
        void queryClient.invalidateQueries({ queryKey: customerKeys.menu(session.sessionId) });
      }
    },
  });
}

export function useUpdateCartItem() {
  const accept = useAcceptOwnCart();
  return useMutation({
    mutationFn: (vars: { cartItemId: string; quantity: number }) =>
      customerApi.updateCartItem(vars.cartItemId, vars.quantity),
    onSuccess: accept,
  });
}

export function useRemoveCartItem() {
  const accept = useAcceptOwnCart();
  return useMutation({
    mutationFn: (cartItemId: string) => customerApi.removeCartItem(cartItemId),
    onSuccess: accept,
  });
}

export function refreshAfterOrderChange(queryClient: QueryClient, sessionId: string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: customerKeys.cart(sessionId) }),
    queryClient.invalidateQueries({ queryKey: customerKeys.orders(sessionId) }),
    queryClient.invalidateQueries({ queryKey: customerKeys.bill(sessionId) }),
  ]);
}

/**
 * Places the shared cart. Never retried automatically and never called without the revision the
 * customer reviewed: a 409 CART_REVISION_CONFLICT must reach the screen so the customer reviews again.
 */
export function usePlaceCartOrder() {
  const { session, lastSeenCart } = useCustomer();
  const queryClient = useQueryClient();
  return useMutation({
    retry: false,
    mutationFn: (vars: { expectedRevision: number; idempotencyKey: string }) =>
      customerApi.placeCartOrder(vars.expectedRevision, vars.idempotencyKey),
    onSuccess: () => {
      lastSeenCart.current = null; // the cart was emptied by the customer's own order
      return refreshAfterOrderChange(queryClient, session.sessionId);
    },
  });
}

export function useCancelOrder() {
  const { session } = useCustomer();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (orderId: string) => customerApi.cancelOrder(orderId),
    // Refresh on success and on failure: a refused cancellation means the order has moved on.
    onSettled: () => refreshAfterOrderChange(queryClient, session.sessionId),
  });
}

export type AssistanceMemory = Partial<Record<AssistanceType, AssistanceOutcome>>;

/** What the server answered to this visit's assistance requests. The backend has no guest read endpoint. */
export function useAssistanceMemory(): AssistanceMemory {
  const { session } = useCustomer();
  return (
    useQuery<AssistanceMemory>({
      queryKey: customerKeys.assistance(session.sessionId),
      queryFn: () => ({}),
      staleTime: Infinity,
      gcTime: Infinity,
      initialData: {},
    }).data ?? {}
  );
}

export function useRequestAssistance() {
  const { session } = useCustomer();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: { type: AssistanceType; note?: string }) =>
      customerApi.requestAssistance(vars.type, vars.note),
    onSuccess: (outcome, vars) => {
      queryClient.setQueryData<AssistanceMemory>(
        customerKeys.assistance(session.sessionId),
        (old) => ({
          ...old,
          [vars.type]: outcome,
        }),
      );
    },
  });
}
